import { SportList } from 'components/SportList/SportList';
import styles from './SportsBetting.module.css';
import { ImageSlider } from 'components/ImageSlider/ImageSlider';
import { useTranslation } from 'react-i18next';
import {
  ChangeEvent,
  KeyboardEvent,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ThemeIcon } from 'components/ThemeIcon/ThemeIcon';
import { MatchList } from 'components/MatchList/MatchList';
import { PhotoData } from 'types/PhotoData';
import { TimeFilter } from 'components/TimeFilter/TimeFilter';
import useAsyncEffect from 'use-async-effect';
import {
  getEventsByLeague,
  getSports,
  searchEventsByTeamName,
} from 'services/api/sportBetsService';
import { Loader } from 'components/Loader/Loader';
import { LeagueContext } from 'context/leagueContext';
import { MatchOffer } from 'types/MatchOffer';
import Modal from 'react-modal';
import { PrematchCatalogContext } from 'context/prematchCatalogContext';
import { SlipContext } from 'context/slipContext';
import { SportsBettingRightSide } from 'components/SportsBettingRightSide/SportsBettingRightSide';
import { DefaultSection } from './DefaultSection';
import {
  ModernSidebarPortal,
  useModernDesktop,
} from 'components/Layout/ModernDesktopShell';
import { Tooltip } from 'react-tooltip';
import { Tooltip as SearchInputTooltip } from 'components/Tooltip/Tooltip';
import { SportsBettingSearchResults } from 'components/SportsBettingSearchResults/SportsBettingSearchResults';

/** Debounced live search; backend uses substring match on team names (`__contains`). */
const SEARCH_DEBOUNCE_MS = 350;
const MIN_SEARCH_QUERY_LENGTH = 2;

const SLIDER_PHOTOS: PhotoData[] = [
  {
    imagePath: '/photos/slider-images/2.jpg',
    bannerKind: 'hero',
    button: {
      buttonText: 'Bet Now',
      url: '/sports-betting',
    },
  },
  {
    imagePath: '/photos/slider-images/2.jpg',
    bannerKind: 'mobile-betting',
  },
  {
    imagePath: '/photos/slider-images/3.jpg',
    bannerKind: 'cash-out',
    button: {
      buttonText: 'Available Soon',
      url: '/live-betting',
    },
  },
  {
    imagePath: '/photos/slider-images/4.jpg',
    bannerKind: 'live-casino',
    button: {
      buttonText: 'Ezugi Casino',
      url: '/live-casino',
    },
  },
  {
    imagePath: '/photos/slider-images/5.jpg',
    bannerKind: 'casino-games',
    button: {
      buttonText: 'Tottalo Casino',
      url: '/casino-games',
    },
  },
];

interface Props {
  hideImageSlider?: boolean;
}
export const SportsBetting = ({ hideImageSlider = false }: Props) => {
  const { t } = useTranslation();
  const modernDesktop = useModernDesktop();
  const [allSports, setAllSports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const { leagues, toTime, fromTime } = useContext(LeagueContext);
  const [leaguesSelection, setLeaguesSelection] =
    useState<{ leagueId: number; sportId: number }[]>(leagues);
  const [leaguesMatches, setLeaguesMatches] = useState<
    { matches: MatchOffer[]; leagueId: number; sportId: number }[]
  >([]);
  const [isFetchingData, setFetchingData] = useState<boolean>(false);
  const [searchResults, setSearchResults] =
    useState<SportsBettingSearchResults | null>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  /** When set, main column shows inline “no results” (no modal). */
  const [emptySearchQuery, setEmptySearchQuery] = useState<string | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchSeqRef = useRef(0);
  const { entries } = useContext(SlipContext);
  const { lastPrematchPush } = useContext(PrematchCatalogContext);
  const prematchCatalogBump = lastPrematchPush?.version ?? 0;

  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  const invalidatePendingSearch = useCallback(() => {
    searchSeqRef.current += 1;
  }, []);

  const executeSearch = useCallback(async (raw: string) => {
    const q = raw.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      setSearchResults(null);
      setEmptySearchQuery(null);
      setTooltipOpen(false);
      return;
    }
    const mySeq = ++searchSeqRef.current;
    try {
      const results = await searchEventsByTeamName(q);
      if (mySeq !== searchSeqRef.current) return;
      setTooltipOpen(false);
      if (results && results.size > 0) {
        setSearchResults({
          query: q,
          results: results as Map<number, MatchOffer[]>,
        });
        setEmptySearchQuery(null);
      } else {
        setSearchResults(null);
        setEmptySearchQuery(q);
      }
    } catch {
      if (mySeq !== searchSeqRef.current) return;
      setSearchResults(null);
      setEmptySearchQuery(q);
    }
  }, []);

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }

    const value = event.target.value;
    const q = value.trim();

    if (q.length === 0) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      setTooltipOpen(false);
      return;
    }

    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      invalidatePendingSearch();
      setSearchResults(null);
      setEmptySearchQuery(null);
      return;
    }

    searchDebounceRef.current = setTimeout(() => {
      searchDebounceRef.current = null;
      const latest = searchInputRef.current?.value ?? value;
      void executeSearch(latest);
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
    const value = (event.target as HTMLInputElement).value;
    const q = value.trim();
    if (q.length < MIN_SEARCH_QUERY_LENGTH) {
      setTooltipOpen(true);
      return;
    }
    void executeSearch(value);
  };

  useAsyncEffect(async () => {
    try {
      const gamesResponse = await getSports(toTime, {
        fromTime: fromTime ?? undefined,
      });
      setIsLoading(false);
      setAllSports(gamesResponse);
    } catch {
      setIsLoading(false);
    }
  }, [toTime, fromTime, prematchCatalogBump]);

  useAsyncEffect(async () => {
    if (leagues.length > leaguesSelection.length) {
      if (searchResults) {
        setSearchResults(null);
        setEmptySearchQuery(null);

        if (searchInputRef.current) {
          searchInputRef.current.value = '';
        }
      }
      setFetchingData(true);
      leagues.forEach(async ({ leagueId, sportId }: any) => {
        if (leaguesSelection.findIndex((x) => x.leagueId === leagueId) === -1) {
          const newLeagueMatches = await getEventsByLeague(
            leagueId,
            sportId,
            toTime,
            fromTime ?? undefined
          );
          if (!newLeagueMatches) {
            setLeaguesMatches([]);
          } else {
            setLeaguesMatches([
              ...leaguesMatches,
              { matches: newLeagueMatches, leagueId, sportId },
            ]);
          }
          setLeaguesSelection([...leagues]);
          setFetchingData(false);
        }
      });
    } else {
      const selectionCopy = [...leaguesSelection];
      const leaguesMatchesCopy = [...leaguesMatches];

      selectionCopy.forEach((e) => {
        if (leagues.indexOf(e) === -1) {
          setLeaguesSelection([...leagues]);
          const leagueIndex = leaguesMatchesCopy.findIndex(
            (lm) => lm.leagueId === e.leagueId
          );
          if (leagueIndex !== -1) {
            leaguesMatchesCopy.splice(leagueIndex, 1);
            setLeaguesMatches([...leaguesMatchesCopy]);
          }
        }
      });
    }
  }, [leagues, toTime, fromTime, prematchCatalogBump]);

  // New prematch data for a sport (pushed over the socket): the open leagues
  // of that sport are fetched again, so prices and matches stay current
  // without a reload.
  const refreshedPushRef = useRef(0);
  useAsyncEffect(
    async (isMounted) => {
      if (
        !lastPrematchPush ||
        lastPrematchPush.version === refreshedPushRef.current
      ) {
        return;
      }
      refreshedPushRef.current = lastPrematchPush.version;
      const stale = leaguesMatches.filter((lm) =>
        lastPrematchPush.sportIds.includes(Number(lm.sportId))
      );
      if (stale.length === 0) return;
      const fresh = await Promise.all(
        stale.map((lm) =>
          getEventsByLeague(
            lm.leagueId,
            lm.sportId,
            toTime,
            fromTime ?? undefined
          )
        )
      );
      if (!isMounted()) return;
      setLeaguesMatches((prev) =>
        prev.map((lm) => {
          const i = stale.findIndex((x) => x.leagueId === lm.leagueId);
          const matches = i >= 0 ? fresh[i] : null;
          return matches ? { ...lm, matches } : lm;
        })
      );
    },
    [lastPrematchPush]
  );

  const searchAndFilter = (
    <div className={styles.searchAndFilter}>
      <div data-tooltip-id="betting-search-error" data-tooltip-place="bottom">
        <input
          type="text"
          placeholder={t('search_label')}
          onChange={handleSearchInputChange}
          onKeyDown={handleSearchKeyDown}
          ref={searchInputRef}
          autoComplete="off"
        />
        {tooltipOpen && (
          <Tooltip
            clickable
            className="blank-tooltip"
            id="betting-search-error"
            arrowColor="#079c20"
            isOpen={tooltipOpen}
          >
            <SearchInputTooltip
              title="Search"
              text={t('betting_search_error')}
              onClose={() => setTooltipOpen(false)}
            />
          </Tooltip>
        )}
      </div>

      <div className={styles.timeFilterWrapper}>
        <TimeFilter />
      </div>
    </div>
  );

  return (
    <div className={styles.container} data-modern-desktop={modernDesktop}>
      {modernDesktop ? (
        <ModernSidebarPortal>
          {isLoading ? <Loader /> : <SportList sports={allSports} />}
        </ModernSidebarPortal>
      ) : (
        <div className={styles.sportsWrapper}>
          {searchAndFilter}
          {isLoading ? <Loader /> : <SportList sports={allSports} />}
        </div>
      )}
      <div className={styles.mainContent}>
        {modernDesktop && searchAndFilter}
        {modernDesktop ? null : hideImageSlider ? (
          <div className={styles.sprotsBettingLabel}>
            {t('sports_betting_label')}
          </div>
        ) : (
          <ImageSlider photos={SLIDER_PHOTOS} />
        )}
        {searchResults ? (
          <SportsBettingSearchResults
            results={searchResults}
            closeSearchResults={() => {
              setSearchResults(null);
              setEmptySearchQuery(null);
            }}
          />
        ) : emptySearchQuery !== null ? (
          <div className={styles.emptySearchMain}>
            <ThemeIcon icon="info-full" alt="" variant="inline" />
            <span>
              {t('no_search_results').replace(
                '{search_query}',
                emptySearchQuery
              )}
            </span>
          </div>
        ) : (
          <>
            {' '}
            {leaguesMatches && leaguesMatches.length > 0 ? (
              leaguesMatches.map((leagueMatches) =>
                leagueMatches.matches.length > 0 ? (
                  <div key={leagueMatches.leagueId}>
                    <MatchList
                      leagueId={leagueMatches.leagueId}
                      leagueName={leagueMatches.matches[0].league.name}
                      key={leagueMatches.leagueId}
                      events={leagueMatches.matches}
                    />
                  </div>
                ) : (
                  <></>
                )
              )
            ) : (
              <DefaultSection />
            )}
          </>
        )}
      </div>
      <div className={styles.rightSide} data-slip-column>
        <SportsBettingRightSide slipEntries={entries} />
      </div>
      <Modal
        isOpen={isFetchingData}
        className={styles.loadingModal}
        ariaHideApp={false}
      >
        <div className={styles.loaderWrapper}>
          {isFetchingData && <Loader />}
        </div>
      </Modal>
    </div>
  );
};
